import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Head from "../components/Head";
import "../styles/Chatbox.css";
import { marked } from "marked";

/* -------------------------------- CONSTANTES ------------------------ */
const API_URL = "https://vinomiam.choisirvin.com/api";

/* Utilisé pour générer un id simple */
const newConvId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

type Message = {
    sender: "user" | "ai";
    text: string;
    options?: { label: string; value: string }[]; // pour les boutons Oui / Non
};

const reportOptions = [
    { id: "offensive", label: "Langage offensant", desc: "Insultes, menaces, racisme, homophobie..." },
    { id: "inappropriate", label: "Contenu inapproprié", desc: "Tonalité déplacée, image suggestive, etc." },
    { id: "wrong_reco", label: "Recommandation erronée", desc: "Le vin proposé est hors sujet." },
    { id: "other", label: "Autre problème", desc: "Non classé ci-dessus" }
];

const Chatbox: React.FC = () => {
    /* ---------------------------- STATES & REFS --------------------------- */
    const [threadID, setThreadID] = useState<string | null>(null);
    const [userInput, setUserInput] = useState<string>("");
    const [uploadedImages, setUploadedImages] = useState<File[]>([]);
    const [messages, setMessages] = useState<Message[]>([
        {
            sender: "ai",
            text: `Bonjour et bienvenue sur Vinomiam, votre Sommelier virtuel 🎉<br/><br/>
Comment puis-je vous aider aujourd'hui à choisir votre accord met et vin parfait ? 🍷<br/><br/>
<ul>
  <li>Si vous ne me connaissez pas encore, essayez-moi avec une petite phrase de type : "Un vin pour aller avec du fromage ?" Et laissez la magie opérer !</li>
  <li>Vous pouvez également consulter la page <a href="/utilisation-vinomiam" target="_blank">Utilisation Vinomiam</a> pour mieux comprendre mon fonctionnement ! 🍇</li>
</ul>`
        }
    ]);
    const [lastSommelierMessage, setLastSommelierMessage] = useState<string>("");
    const [isAiTyping, setIsAiTyping] = useState<boolean>(false);
    const [convId, setConvId] = useState<string | null>(null);
    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            setUploadedImages((prev) => [...prev, ...files]);
            e.target.value = ""; // 👈 reset du champ input
        }
    };
    const [zoomImage, setZoomImage] = useState<string | null>(null);
    const [persistedImages, setPersistedImages] = useState<File[]>([]);
    const wineDataRef = useRef<any[]>([]);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const [confirmUsePersistedImage, setConfirmUsePersistedImage] = useState<boolean | null>(null);
    const [pendingQuestion, setPendingQuestion] = useState<string>("");
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportMessageIndex, setReportMessageIndex] = useState<number | null>(null);
    const [selectedReportReason, setSelectedReportReason] = useState<string>("");
    const [reportDescription, setReportDescription] = useState("");
    const [reportSuccessMsg, setReportSuccessMsg] = useState("");

    /* ---------------------------- HELPERS --------------------------- */
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: "smooth", block: "nearest"});
    };

    useEffect(() => {
        const ua = navigator.userAgent;
        const isWebView = /(FBAN|FBAV|Instagram|WebView|Line|Messenger)/i.test(ua);
        if (isWebView) {
            alert("⚠️ Pour envoyer des images, ouvre ce site dans ton navigateur (Chrome ou Safari) car les applications comme Instagram ou Facebook peuvent bloquer cette fonctionnalité.");
        }
    }, []);

    /* ----------------------- HISTORIQUE LOCALSTORAGE ---------------------- */
    /** Nettoie le HTML pour extraire un titre court  */
    const plainText = (html: string) => html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();

    /* À chaque changement de messages, on met à jour la conversation courante */
    useEffect(() => {
        // Pas de sauvegarde si aucune intervention utilisateur
        if (!messages.some((m) => m.sender === "user")) return;

        let id = convId;
        if (!id) {
            id = newConvId();
            setConvId(id);
        }

        const storedRaw = localStorage.getItem("vinomiam-conversations");
        const conversations: any[] = storedRaw ? JSON.parse(storedRaw) : [];

        const idx = conversations.findIndex((c) => c.id === id);
        const title = plainText(messages.find((m) => m.sender === "user")!.text).slice(0, 60);
        const convObj = {id, title, messages};

        if (idx > -1) conversations[idx] = convObj;
        else conversations.push(convObj);

        localStorage.setItem("vinomiam-conversations", JSON.stringify(conversations));
    }, [messages, convId]);

    /* ------------------------------ EFFECTS ------------------------------ */
    useEffect(scrollToBottom, [messages]);

    /* Dark‑mode auto */
    useEffect(() => {
        const darkModeMq = window.matchMedia("(prefers-color-scheme: dark)");
        const toggle = (e: MediaQueryListEvent) => document.body.classList.toggle("dark", e.matches);
        document.body.classList.toggle("dark", darkModeMq.matches);
        darkModeMq.addEventListener("change", toggle);
        return () => darkModeMq.removeEventListener("change", toggle);
    }, []);

    /* Initialise un thread OpenAI + charge JSON vins */
    useEffect(() => {
        /* ---- thread ID ---- */
        fetch(`${API_URL}/init-thread`, {method: "POST"})
            .then((r) => r.json())
            .then((d) => setThreadID(d.threadId))
            .catch((e) => console.error("Erreur threadID :", e));

        /* ---- charge les vins ---- */
        const loadWines = async () => {
            const cats = [
                "rouge",
                "blanc",
                "rose",
                "effervescent",
                "mute_VDN_VDL",
                "jaune_paille",
                "orange",
                "petits_formats",
                "grands_formats"
            ];
            const all: any[] = [];
            for (const cat of cats) {
                try {
                    const res = await fetch(`https://vinomiam.choisirvin.com/data/DB_vin_${cat}.json`);
                    const json = await res.json();
                    const cleaned = json.map((w: any) => ({
                        ...w,
                        NomDeCuvee: w.NomDeCuvee ?? "",
                        Millesime:
                            w.Millesime?.toLowerCase().includes("pas de") || w.Millesime?.toLowerCase() === "null"
                                ? ""
                                : w.Millesime
                    }));
                    all.push(...cleaned);
                } catch (e) {
                    console.error(`Erreur chargement JSON ${cat}:`, e);
                }
            }
            wineDataRef.current = all;
        };
        loadWines();
    }, []);

    /* ------------------------------ DISPATCH ------------------------------ */
    const callDispatcher = async (threadId: string | null, question: string, context: string) => {
        const res = await fetch(`${API_URL}/ask-dispatcher`, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({question, threadId, context})
        });
        return res.json();
    };

    /* ------------------------- CAVISTE POST‑TRAIT. ------------------------- */
    const processWineLinks = async (text: string): Promise<string> => {
        const wines = wineDataRef.current;
        let sommelierPart = "";
        let cavistePart = "";

        const firstH2 = text.match(/<h2>[\s\S]*?<\/h2>/);
        if (firstH2) {
            const idx = text.indexOf(firstH2[0]);
            sommelierPart = text.substring(0, idx).trim();
            cavistePart = text.substring(idx).trim();
        } else {
            sommelierPart = text;
        }

        const updatedLines: string[] = [];
        const lines = cavistePart.split(/\r?\n/);

        const cleaned = (s: string | null | undefined): string =>
            (s ?? "")
                .toLowerCase()
                .replace(/\*\*/g, "")
                .replace(/[‐\-―−﹘﹣－-]/g, "-")
                .replace(/[.,;:!?()【】\[\]"”“’‘]/g, "")
                .replace(/\s+/g, " ")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .trim();

        const extractPreviousWines = (): string[] => {
            const wineRegex = /<h2>(.*?)<\/h2>/g;
            const seen = new Set<string>();
            messages.forEach((msg) => {
                if (msg.sender === "ai") {
                    let match: RegExpExecArray | null;
                    while ((match = wineRegex.exec(msg.text))) {
                        seen.add(match[1].trim());
                    }
                }
            });
            return Array.from(seen);
        };

        const matched = new Set<string>(extractPreviousWines());
        for (let i = 0; i < lines.length; i++) {
            const l = lines[i];
            const cl = cleaned(l);
            let ok = false;

            for (const w of wines) {
                const hit =
                    cl.includes(cleaned(w.NomDuDomaine)) &&
                    cl.includes(cleaned(w.Millesime)) &&
                    (w.NomDeCuvee ? cl.includes(cleaned(w.NomDeCuvee)) : true);
                if (hit) {
                    const label = `${w.TypeDeProduit} - ${w.NomDuDomaine} – ${w.NomDeCuvee || "Pas de nom de cuvée"} - ${w.Millesime} - ${w.Appellation}`.trim();
                    if (matched.has(label)) {
                        ok = true;
                        break;
                    }
                    matched.add(label);
                    const infoId = `info-${Math.random().toString(36).substring(2, 9)}`;
                    const htmlBlock = `
<div class="wine-suggestion">
  <h3>${label}</h3>
  <p><a href="${w.Lien}" target="_blank">🍷 Retrouver ce vin juste ici ! 🍇</a></p>
  <p>
    <span onclick="(function(){const el=document.getElementById('${infoId}');if(el){el.style.display=el.style.display==='block'?'none':'block';}})()" style="cursor:pointer;color:#0066cc;">
      <small>ℹ️ Plus d'informations</small>
    </span>
  </p>
  <div id="${infoId}" style="display:none;border:1px solid #ccc;padding:15px;border-radius:6px;font-size:13px;margin-top:8px;">
    Le lien du vin peut évoluer sans nuire à l'accord. Si c'est le cas, demandez un autre vin et contactez <strong>millesimhall@gmail.com</strong>.
  </div>
</div>`;

                    updatedLines.push(`<h2>${label}</h2>`);
                    let j = i + 1;
                    while (j < lines.length && lines[j].trim() !== "") {
                        updatedLines.push(lines[j]);
                        j++;
                    }
                    updatedLines.push(htmlBlock);
                    i = j;
                    ok = true;
                    break;
                }
            }
            if (!ok) updatedLines.push(l);
        }

        if (updatedLines.length === 0 && cavistePart) {
            updatedLines.push(
                `<p style="color:#cc0000;font-style:italic;">⚠️ Aucun vin proposé n'existe dans la base actuelle.</p>`
            );
        }

        return sommelierPart ? `${sommelierPart}<br/><br/>${updatedLines.join("<br/>")}` : updatedLines.join("<br/>");
    };

    /* --------------------------- ENVOI DU MESSAGE -------------------------- */

    const sendMessage = async () => {
        const hasText = userInput.trim().length > 0;
        const hasImages = uploadedImages.length > 0;
        const hasPersisted = persistedImages.length > 0;

        // Question actuelle
        const question = userInput.trim();
        if (!hasText && !hasImages && !(hasPersisted && confirmUsePersistedImage)) return;

        // Gestion du cas : image persistée mais confirmation manquante
        if (hasText && !hasImages && hasPersisted && confirmUsePersistedImage === null) {
            setPendingQuestion(question);
            setMessages((prev) => [
                ...prev,
                {
                    sender: "ai",
                    text: "Souhaitez-vous que je prenne en compte l’image envoyée précédemment pour cette nouvelle question ?",
                    options: [
                        { label: "Oui", value: "yes" },
                        { label: "Non", value: "no" }
                    ]
                }
            ]);
            return;
        }

        // Affichage utilisateur
        let messageHtml = "";
        if (hasText) messageHtml += `<p>${question}</p>`;
        if (hasImages) {
            const imgTags = uploadedImages.map((file) => {
                const url = URL.createObjectURL(file);
                return `<img src="${url}" alt="${file.name}" style="max-width: 200px; max-height: 150px; margin: 5px; border-radius: 6px;" />`;
            }).join("");
            messageHtml += `<div class="chat-images">${imgTags}</div>`;
        }
        setMessages((prev) => [...prev, { sender: "user", text: messageHtml || "📷 Image envoyée" }]);
        setUserInput("");
        setIsAiTyping(true);

        try {
            if (hasImages || (hasPersisted && confirmUsePersistedImage)) {
                const formData = new FormData();
                formData.append("threadId", threadID || "");
                formData.append("question", question);

                if (confirmUsePersistedImage) {
                    persistedImages.forEach((file) => formData.append("images", file));
                }
                uploadedImages.forEach((file) => formData.append("images", file));

                const res = await fetch(`${API_URL}/ask-sommelier-with-image`, {
                    method: "POST",
                    body: formData
                });

                const data = await res.json();
                const somHTML = (await marked.parse(data.text)) as string;
                const enriched = await processWineLinks(somHTML);
                setMessages((p) => [...p, { sender: "ai", text: enriched }]);
                setLastSommelierMessage(data.text || question);

                if (hasImages) {
                    setPersistedImages((prev) => [...prev, ...uploadedImages]);
                    setUploadedImages([]);
                }

                setConfirmUsePersistedImage(null);
                return;
            }

            // Texte seul → dispatcher
            const dispatch = await callDispatcher(threadID, question, lastSommelierMessage);
            const { cleaned_prompt, threadId: returnedThreadId, action } = dispatch;
            const currentThreadId = returnedThreadId || threadID;
            setThreadID(currentThreadId);

            let sommelierText = "";

            if (action.includes("sommelier")) {
                const resSom = await fetch(`${API_URL}/ask-sommelier`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ threadId: currentThreadId, question: cleaned_prompt })
                });

                const dataSom = await resSom.json();
                sommelierText = dataSom.text || "Erreur Sommelier.";
                setLastSommelierMessage(sommelierText);

                const somHTML = (await marked.parse(sommelierText)) as string;
                const enrichedSom = await processWineLinks(somHTML);
                setMessages((p) => [...p, { sender: "ai", text: enrichedSom }]);
            }

            if (action.includes("caviste")) {
                const resCav = await fetch(`${API_URL}/ask-caviste`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        threadId: currentThreadId,
                        context: sommelierText || lastSommelierMessage || cleaned_prompt,
                        previousWines: []
                    })
                });

                const dataCav = await resCav.json();
                const finalMsg = await processWineLinks(dataCav.text || "Erreur Caviste.");
                setMessages((p) => [...p, { sender: "ai", text: finalMsg }]);
            }
        } catch (e) {
            console.error("Erreur IA :", e);
            alert("Erreur de traitement.");
        } finally {
            setIsAiTyping(false);
            setConfirmUsePersistedImage(null);
            setPendingQuestion("");
        }
    };

    const handleOptionResponse = async (choice: string) => {
        const useImage = choice === "yes";
        setConfirmUsePersistedImage(useImage);
        if (!useImage) {
            setPersistedImages([]); // on vide le contexte image
        }
        setMessages((prev) => prev.filter((msg) => !msg.options));

        // On réinjecte la question qui était en attente
        setUserInput(pendingQuestion);

        // Attendre le state update avant d'appeler sendMessage
        setTimeout(() => {
            sendMessage(); // relance normale
        }, 50);
    };


    /* -------------------------- HANDLER ENTER -------------------------- */

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    /* ------------------------------ RENDER ------------------------------ */

    return (
        <div className="App">
            <Head />
            <div className="chatbox-container">
                <div className="chatbox">
                    <div className="messages-container">
                        {messages.map((m, idx) => (
                            <div key={idx} className={`message ${m.sender}`}>
                                <div
                                    className="message-content"
                                    dangerouslySetInnerHTML={{ __html: m.text }}
                                    onClick={(e) => {
                                        const target = e.target as HTMLElement;
                                        if (target.tagName === "IMG") {
                                            setZoomImage((target as HTMLImageElement).src);
                                        }
                                    }}
                                />

                                {m.sender === "ai" && (
                                    <button
                                        onClick={() => {
                                            setShowReportModal(true);
                                            setReportMessageIndex(idx);
                                        }}
                                        className="report-button"
                                        title="Signaler ce message"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle cx="12" cy="5" r="2"/>
                                            <circle cx="12" cy="12" r="2"/>
                                            <circle cx="12" cy="19" r="2"/>
                                        </svg>
                                    </button>
                                )}

                                {m.options && (
                                    <div className="chat-option-buttons">
                                        {m.options.map((opt) => (
                                            <button
                                                key={opt.value}
                                                onClick={() => handleOptionResponse(opt.value)}
                                                className="option-button"
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {isAiTyping && (
                            <div className="message ai typing-indicator">
                                <span className="dot"></span>
                                <span className="dot"></span>
                                <span className="dot"></span>
                            </div>
                        )}
                        <div ref={messagesEndRef}></div>
                    </div>

                    <div className="input-container-modern">
                    <textarea
                        ref={inputRef}
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Votre message..."
                        rows={1}
                        disabled={isAiTyping}
                    ></textarea>

                        <div className="input-actions">
                            <div className="tooltip-wrapper">
                                <label htmlFor="fileInput" className="icon-button" title="Ajouter une image">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                        <circle cx="12" cy="13" r="4" />
                                    </svg>
                                </label>
                                <input
                                    id="fileInput"
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    capture="environment"
                                    onChange={handleImageUpload}
                                    disabled={isAiTyping}
                                    style={{
                                        opacity: 0,
                                        position: 'absolute',
                                        width: '0.1px',
                                        height: '0.1px',
                                        zIndex: -1
                                    }}
                                />
                                <div className="tooltip-bubble">
                                    🆕 Nouvelle fonctionnalité ! Peut encore présenter des incohérences. 📸
                                </div>
                            </div>

                            {uploadedImages.length > 0 && (
                                <div className="image-preview-list">
                                    {uploadedImages.map((img, idx) => (
                                        <div key={idx} className="image-preview-item">
                                            <img
                                                src={URL.createObjectURL(img)}
                                                alt={img.name}
                                                className="image-thumb"
                                            />
                                            <button
                                                className="remove-btn"
                                                onClick={() =>
                                                    setUploadedImages((prev) =>
                                                        prev.filter((_, i) => i !== idx)
                                                    )
                                                }
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button onClick={sendMessage} disabled={isAiTyping} className="icon-button">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    viewBox="0 0 24 24"
                                >
                                    <line x1="22" y1="2" x2="11" y2="13"></line>
                                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                </svg>
                            </button>
                            {zoomImage && (
                                <div className="image-modal" onClick={() => setZoomImage(null)}>
                                    <img src={zoomImage} alt="Zoom" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* MODAL DE SIGNALEMENT */}
            {showReportModal && reportMessageIndex !== null && (
                <div className="modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div className="modal-content" style={{
                        background: '#fff', padding: '20px', borderRadius: '8px', width: '400px', maxWidth: '90%'
                    }}>
                        <h2>Signaler ce message</h2>
                        <p style={{ fontSize: '14px' }}>Choisissez un motif :</p>
                        {reportOptions.map((opt) => (
                            <label key={opt.id} style={{ display: 'block', margin: '8px 0' }}>
                                <input
                                    type="radio"
                                    name="reportReason"
                                    value={opt.id}
                                    checked={selectedReportReason === opt.id}
                                    onChange={() => setSelectedReportReason(opt.id)}
                                />
                                <strong> {opt.label} :</strong> <small>{opt.desc}</small>
                            </label>
                        ))}
                        <textarea
                            placeholder="Décrire ce qu’il s’est passé."
                            value={reportDescription}
                            onChange={(e) => setReportDescription(e.target.value)}
                            style={{ width: '100%', height: '80px', marginTop: '10px' }}
                        />
                        {reportSuccessMsg && (
                            <div style={{ marginTop: "10px", color: "#0c0", fontWeight: "bold", fontSize: "14px" }}>
                                {reportSuccessMsg}
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '15px' }}>
                            <button onClick={() => setShowReportModal(false)}>Annuler</button>
                            <button
                                onClick={async () => {
                                    await fetch(`${API_URL}/report`, {
                                        method: "POST",
                                        headers: { "Content-Type": "application/json" },
                                        body: JSON.stringify({
                                            message: messages[reportMessageIndex].text,
                                            reason: selectedReportReason || "other",
                                            description: reportDescription
                                        })
                                    });

                                    setReportSuccessMsg("✅ Signalement bien envoyé, merci !");

                                    setTimeout(() => {
                                        setShowReportModal(false);
                                        setSelectedReportReason("");
                                        setReportDescription("");
                                        setReportMessageIndex(null);
                                        setReportSuccessMsg("");
                                    }, 2000);
                                }}
                            >
                                Signaler
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );

}
export default Chatbox;
